import { useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';

const SIGNED_OUT = Object.freeze({ personal: null, legacy: null });

// Personal (Routora V2) and legacy products (Teams, the extension) keep
// separate Firebase sessions. Each is loaded only when a page asks.
const PRODUCT_AUTH = Object.freeze({
  personal: () => import('../v2/shared/auth/v2Firebase').then((module) => module.v2Auth),
  legacy: () => import('../legacyFirebase').then((module) => module.legacyAuth),
});

// Observes who is signed in to each product. It never loads either product's
// account provider, so nothing is provisioned, bootstrapped or redirected.
export default function useProductSessions({ enabled = true, productAuth = PRODUCT_AUTH } = {}) {
  const [sessions, setSessions] = useState(SIGNED_OUT);

  useEffect(() => {
    if (!enabled) return undefined;

    let active = true;
    const unsubscribes = [];

    Object.entries(productAuth).forEach(([product, loadAuth]) => {
      Promise.resolve()
        .then(loadAuth)
        .then((auth) => {
          if (!active) return;
          unsubscribes.push(onAuthStateChanged(auth, (user) => {
            if (active) setSessions((current) => ({ ...current, [product]: user || null }));
          }));
        })
        .catch(() => {
          // A product that isn't configured in this environment has no session.
        });
    });

    return () => {
      active = false;
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  }, [enabled, productAuth]);

  return enabled ? sessions : SIGNED_OUT;
}
